import { BaseRouteParams } from 'hocs'
import { isNull, omitBy } from 'lodash'
import React, { useMemo, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'
import { KebabMenu } from 'uiComponents/menus/kebabMenu'
import { ConfirmationDialog } from 'uiComponents/popups/confirmationDialog'
import { LucideIconName } from 'utils/lucideIcon'
import { NoResultsRow } from 'uiComponents/table/noResultsRow'
import { ReactTable, TableColumn } from 'uiComponents/table/reactTable'
import { useVenueLocations } from '../../../tapPlacements/studio/helpers'
import { useSelectedParkMapContext } from '../context/selectedParkMap'
import { Parkmap } from '../models'
import { useCreateParkMapMutation, useDeleteParkMapMutation } from '../reduxQuery'
import './styles.scss'
import { copyQrCodeToClipboard, copyMapUrlToClipboard } from './utils'
import Badge from 'uiComponents/badge'
import { useTranslation } from 'translations'

const ActivityContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.5em;
    padding: 0.25em 0;
`

interface ParkMapListProps {
    parkmaps: Parkmap[]
}

const ParkMapList = ({ parkmaps }: ParkMapListProps) => {
    const { t } = useTranslation()
    const { accountSlug } = useParams<BaseRouteParams>()
    const history = useHistory()
    const [parkMapToDelete, setParkMapToDelete] = useState<Parkmap>()
    const [deleteParkMap, { isLoading }] = useDeleteParkMapMutation()
    const [createParkMap] = useCreateParkMapMutation()
    const theme = useTheme()
    const { setSelectedParkMap } = useSelectedParkMapContext()
    const venueLocations = useVenueLocations(accountSlug)

    const onDeleteParkMap = () => {
        if (parkMapToDelete) {
            deleteParkMap({ parkMap: parkMapToDelete })
            setParkMapToDelete(undefined)
        }
    }

    const copyParkMap = (parkMap: Parkmap) => {
        const parkMapCopy = omitBy(parkMap, isNull) as Parkmap

        createParkMap({
            parkMap: {
                ...parkMapCopy,
                name: `${parkMapCopy.name} copy`,
                enabled: false,
            },
        })
    }

    const columns: TableColumn<Parkmap>[] = useMemo(
        () =>
            [
                {
                    accessor: 'name',
                    Header: t('venue.parkMap.list.columns.name'),
                    Cell: ({ row: { original } }) => {
                        return (
                            <>
                                {original.enabled ? (
                                    <ActivityContainer>
                                        {original.name}
                                        <Badge variant="success" label={t('venue.parkMap.list.activeBadge')} />
                                    </ActivityContainer>
                                ) : (
                                    original.name
                                )}
                            </>
                        )
                    },
                },
                {
                    accessor: 'locationId',
                    Header: t('venue.parkMap.list.columns.location'),
                    Cell: ({ row: { original } }) => {
                        return <>{venueLocations.find((location) => location.value === original.locationId)?.name}</>
                    },
                },
                {
                    accessor: 'action',
                    disableHideColumn: true,
                    disableSortBy: true,
                    align: 'right',
                    style: {
                        overflow: 'visible',
                    },
                    Cell: ({ row: { original } }) => {
                        const options = [
                            {
                                label: t('common.edit'),
                                icon: 'Pencil' as LucideIconName,
                                onClick: () => {
                                    setSelectedParkMap(original)
                                    history.push(`/account/${accountSlug}/venue/park_map/${original.id}/details`)
                                },
                            },
                            {
                                label: t('venue.parkMap.list.actions.copyUrl'),
                                icon: 'ClipboardCopy' as LucideIconName,
                                onClick: () => copyMapUrlToClipboard(original, accountSlug),
                            },
                            {
                                label: t('venue.parkMap.list.actions.copyQr'),
                                icon: 'ScanQrCode' as LucideIconName,
                                onClick: () => copyQrCodeToClipboard(original, accountSlug),
                            },
                            {
                                label: t('venue.parkMap.list.actions.duplicate'),
                                icon: 'Copy' as LucideIconName,
                                onClick: () => copyParkMap(original),
                            },
                            {
                                label: t('common.delete'),
                                icon: 'Trash' as LucideIconName,
                                onClick: () => setParkMapToDelete(original),
                            },
                        ]
                        return <KebabMenu options={options} listAnchor="right" />
                    },
                },
            ] as TableColumn<Parkmap>[],
        [accountSlug, history, setSelectedParkMap, venueLocations, t],
    )

    return (
        <>
            {parkMapToDelete && (
                <ConfirmationDialog
                    title={t('venue.parkMap.list.deleteConfirmTitle')}
                    text={t('venue.parkMap.list.deleteConfirmText')}
                    buttonText={t('common.delete')}
                    loading={isLoading}
                    onCancel={() => setParkMapToDelete(undefined)}
                    onConfirm={onDeleteParkMap}
                />
            )}
            <ReactTable
                noResultsRow={() => <NoResultsRow text={t('venue.parkMap.list.emptyState')} />}
                loading={false}
                elevation={false}
                rowProps={() => ({
                    style: {
                        borderBottom: `1px solid ${theme.colors.aluminiumShades[5]}`,
                    },
                })}
                tableProps={{
                    className: 'park-map-table react__table--card',
                }}
                data={parkmaps}
                totalCount={parkmaps.length}
                columns={columns}
                storeStateInQuery={false}
            />
        </>
    )
}

export default ParkMapList
